/**
 * Text matching for the app's search boxes.
 *
 * Core stores merchant names exactly as they were typed at signup, and a lot of
 * them carry doubled internal spaces — `عالمك··الافتراضي`, `Agoo··Baby`,
 * `مجموعة·ليلي···للتسوق`. HTML collapses runs of whitespace, so the officer sees
 * (and copies) `عالمك الافتراضي` with one space. A raw `includes()` then fails
 * against the stored two-space form, and the row is simply absent from the list.
 *
 * That is how a 188-order pickup in Zone C went missing from Add Stop to Route on
 * 2026-08-04 while sitting in the API response the modal had already loaded. On
 * prod that day 39 of 252 open tasks — 21 merchants, 1,509 orders — could not be
 * found by typing the name the screen was showing.
 *
 * Both sides get normalised. Normalising only the haystack still fails when the
 * officer pastes the stored double-space form back in.
 *
 * Deliberately NOT fixed by rewriting the names in Core: Fleet does not own that
 * data, and the gap is between storage and rendering, which is the search's to
 * absorb.
 */

/**
 * Lower-cased, whitespace-collapsed, trimmed. `\s+` covers the space, tab and
 * newline runs actually seen in Core; it is not a Unicode confusable folder and
 * does not try to be one.
 */
export function normalizeForSearch(value: string | null | undefined): string {
  if (!value) return '';
  return value.toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * True when `query` occurs in any of `fields`, comparing normalised forms.
 * An empty query matches — callers guard for that themselves, and returning
 * false here would blank every list on first render.
 */
export function matchesSearch(query: string, ...fields: (string | null | undefined)[]): boolean {
  const q = normalizeForSearch(query);
  if (!q) return true;
  return fields.some((field) => normalizeForSearch(field).includes(q));
}
